import { useUnifiedStatus } from "@/contexts/UnifiedStatusContext";
import { UnifiedStatusBadge } from "@/components/shared/UnifiedStatusBadge";
import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Heart, ChevronRight, Gift, Star, Sparkles, X, ChevronLeft, Check, Bookmark } from "lucide-react";

const MiniLogo = ({ name, size = "lg" }) => {
  const colors = ["bg-sky-400", "bg-purple-400", "bg-green-400", "bg-orange-400", "bg-pink-400", "bg-indigo-400", "bg-teal-400", "bg-red-400"];
  const i = name ? name.charCodeAt(0) % colors.length : 0;
  const s = size === "sm" ? "w-10 h-10 text-sm" : size === "lg" ? "w-20 h-20 text-3xl" : "w-14 h-14 text-lg";
  return <div className={`${s} rounded-3xl ${colors[i]} flex items-center justify-center flex-shrink-0 shadow-lg`}><span className="text-white font-bold">{name?.charAt(0)?.toUpperCase()}</span></div>;
};

const SwipeCard = ({ offer, onLike, onSkip, onTap, isFavorited }) => (
  <motion.div
    layout
    className="absolute inset-0 bg-white rounded-3xl shadow-[0_20px_60px_rgba(0,0,0,0.12)] p-6 flex flex-col"
    drag="x"
    dragConstraints={{ left: 0, right: 0 }}
    dragElastic={0.7}
    onDragEnd={(_, info) => {
      if (info.offset.x > 100) onLike();
      else if (info.offset.x < -100) onSkip();
    }}
    initial={{ scale: 0.95, opacity: 0 }}
    animate={{ scale: 1, opacity: 1 }}
    exit={{ x: 300, opacity: 0, rotate: 15 }}
    transition={{ type: "spring", stiffness: 300, damping: 25 }}
  >
    {/* Card content */}
    <div className="flex-1 flex flex-col items-center justify-center text-center" onClick={onTap}>
      <MiniLogo name={offer.name} />
      <h2 className="text-2xl font-extrabold text-slate-900 mt-5" style={{ fontFamily: "'Outfit', sans-serif" }}>{offer.name}</h2>
      {getStatus(offer.business_id) && <div className="mt-1"><UnifiedStatusBadge status={getStatus(offer.business_id)} /></div>}
      <p className="text-slate-500 text-sm mt-2 leading-relaxed max-w-[240px]">{offer.offer_title}</p>

      <div className="mt-6 flex items-center gap-6">
        <div className="text-center">
          <p className="text-3xl font-extrabold text-green-600" style={{ fontFamily: "'Outfit', sans-serif" }}>${offer.offer_value_est}</p>
          <p className="text-xs text-slate-400 mt-1">value</p>
        </div>
        <div className="w-px h-10 bg-slate-200" />
        <div className="text-center">
          <div className="flex gap-0.5 justify-center">
            {[1,2,3,4,5].map(s => (
              <Star key={s} className={`w-4 h-4 ${s <= offer.ease_stars ? "fill-amber-400 text-amber-400" : "text-slate-200"}`} />
            ))}
          </div>
          <p className="text-xs text-slate-400 mt-1">ease</p>
        </div>
      </div>

      <p className="text-xs text-slate-400 mt-4">{offer.category} &middot; {offer.coverage}</p>

      {offer.offer_requires_app === false && (
        <span className="mt-3 px-3 py-1 bg-purple-50 text-purple-600 rounded-full text-xs font-semibold">No App Needed</span>
      )}
    </div>

    {/* Action buttons */}
    <div className="flex items-center justify-center gap-6 pb-2">
      <button onClick={onSkip} className="w-14 h-14 rounded-full bg-slate-100 flex items-center justify-center text-slate-400 hover:bg-red-50 hover:text-red-400 transition-colors shadow-sm" data-testid="swipe-skip">
        <X className="w-6 h-6" />
      </button>
      <button onClick={onTap} className="w-16 h-16 rounded-full bg-sky-500 flex items-center justify-center text-white shadow-lg shadow-sky-500/30 hover:bg-sky-600 transition-colors" data-testid="swipe-details">
        <ChevronRight className="w-7 h-7" />
      </button>
      <button onClick={onLike} className="w-14 h-14 rounded-full bg-slate-100 flex items-center justify-center shadow-sm hover:bg-red-50 transition-colors" data-testid="swipe-like">
        <Heart className={`w-6 h-6 ${isFavorited ? "fill-red-500 text-red-500" : "text-slate-400 hover:text-red-400"}`} />
      </button>
    </div>
  </motion.div>
);

export const SwipeView = ({ data, user, onOfferClick }) => {
  const { getStatus } = useUnifiedStatus();
  const { offers, stats, favoriteIds, toggleFavorite } = data;
  const [currentIndex, setCurrentIndex] = useState(0);
  const [saved, setSaved] = useState(0);
  const [skipped, setSkipped] = useState(0);
  const totalValue = offers.reduce((s, o) => s + (o.offer_value_est || 0), 0);

  const currentOffer = offers[currentIndex];
  const progress = offers.length > 0 ? ((currentIndex) / offers.length) * 100 : 0;

  const handleLike = () => {
    if (currentOffer) toggleFavorite(currentOffer.business_id);
    setSaved(s => s + 1);
    setCurrentIndex(i => Math.min(i + 1, offers.length));
  };

  const handleSkip = () => {
    setSkipped(s => s + 1);
    setCurrentIndex(i => Math.min(i + 1, offers.length));
  };

  const handleTap = () => {
    if (currentOffer) onOfferClick(currentOffer);
  };

  const handleBack = () => {
    setCurrentIndex(i => Math.max(i - 1, 0));
  };

  return (
    <div className="min-h-screen bg-slate-50 pb-28 flex flex-col" style={{ fontFamily: "'DM Sans', sans-serif" }}>
      {/* Header */}
      <div className="px-6 pt-6 pb-4">
        <div className="flex items-center justify-between mb-4">
          <div>
            <p className="text-slate-400 text-xs">Discover your birthday deals</p>
            <h1 className="text-xl font-extrabold text-slate-900" style={{ fontFamily: "'Outfit', sans-serif" }}>
              {stats.days_until_birthday} days left
            </h1>
          </div>
          <div className="flex items-center gap-3 text-sm">
            <span className="flex items-center gap-1 text-red-500"><Heart className="w-3.5 h-3.5 fill-red-500" /> {saved}</span>
            <span className="text-slate-300">|</span>
            <span className="text-slate-400">{skipped} skipped</span>
          </div>
        </div>

        {/* Progress bar */}
        <div className="bg-slate-200 rounded-full h-1.5 overflow-hidden">
          <motion.div
            animate={{ width: `${progress}%` }}
            className="h-full bg-sky-500 rounded-full"
          />
        </div>
        <p className="text-[10px] text-slate-400 mt-1">{currentIndex} of {offers.length} reviewed</p>
      </div>

      {/* Swipe area */}
      <div className="flex-1 px-6 relative" style={{ minHeight: 420 }}>
        {currentIndex < offers.length ? (
          <AnimatePresence mode="popLayout">
            <SwipeCard
              key={currentOffer.business_id}
              offer={currentOffer}
              onLike={handleLike}
              onSkip={handleSkip}
              onTap={handleTap}
              isFavorited={favoriteIds.has(currentOffer.business_id)}
            />
          </AnimatePresence>
        ) : (
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="absolute inset-0 flex flex-col items-center justify-center text-center"
          >
            <Sparkles className="w-12 h-12 text-sky-500 mb-4" />
            <h2 className="text-xl font-bold text-slate-800">All done!</h2>
            <p className="text-sm text-slate-500 mt-2">You've reviewed all {offers.length} birthday deals.</p>
            <p className="text-sm text-slate-500">Saved <strong className="text-red-500">{saved}</strong>, skipped {skipped}.</p>
            <p className="text-lg font-bold text-green-600 mt-3">${totalValue.toFixed(0)} potential haul</p>
            <button
              onClick={() => { setCurrentIndex(0); setSaved(0); setSkipped(0); }}
              className="mt-6 px-6 py-3 bg-sky-500 text-white rounded-full font-medium shadow-lg"
              data-testid="swipe-restart"
            >
              Start Over
            </button>
          </motion.div>
        )}
      </div>

      {/* Back button */}
      {currentIndex > 0 && currentIndex < offers.length && (
        <div className="px-6 pb-4 flex justify-center">
          <button onClick={handleBack} className="flex items-center gap-1 text-xs text-slate-400 hover:text-slate-600 transition-colors" data-testid="swipe-back">
            <ChevronLeft className="w-3 h-3" /> Go back
          </button>
        </div>
      )}
    </div>
  );
};
